// CategoryLayout — generic two-panel layout for category-based domains.
// Left panel: read-only category tree for selection.
// Right panel: child route via Outlet (list or detail).
//
// Parameterised by categoryType, domainPath, and heading.
// Used by ControlLayout, ActionLayout, EventLayout, and ObligationLayout.

import { useState, useCallback, useEffect, useRef, createContext, useContext } from 'react'
import { Outlet, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { categoriesApi, type CategoryType } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { SelectionCategoryTree } from '@/components/features/category-selection-tree/SelectionCategoryTree'
import { ResizablePanel } from '@/components/shared/ResizablePanel'

// --- Category selection context ---

export interface CategorySelection {
  categoryId: string | null
  categoryName: string
}

const CategorySelectionContext = createContext<CategorySelection>({
  categoryId: null,
  categoryName: '',
})

/** Access the currently selected category from any child route. */
export function useSelectedCategory() {
  return useContext(CategorySelectionContext)
}

// --- Config for each domain ---

export interface CategoryLayoutConfig {
  /** Which category type to fetch (control, event, action, framework). */
  categoryType: CategoryType
  /** URL path segment for navigation, e.g. 'controls', 'actions'. */
  domainPath: string
  /** Heading shown above the tree, e.g. 'Control Categories'. */
  heading: string
  /** Optional action element rendered in the sidebar header (e.g. an Add button). */
  headerAction?: React.ReactNode
}

// --- Layout component ---

export function CategoryLayout({ categoryType, domainPath, heading, headerAction }: CategoryLayoutConfig) {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const api = useApi()
  const [selected, setSelected] = useState<CategorySelection>({
    categoryId: null,
    categoryName: '',
  })

  // Query tree data at layout level for auto-selection of root node
  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.tree(slug, categoryType),
    queryFn: () => categoriesApi(api, categoryType).tree(),
  })

  // Auto-select root node on first load
  const hasAutoSelected = useRef(false)
  useEffect(() => {
    if (categories && categories.length > 0 && !hasAutoSelected.current) {
      hasAutoSelected.current = true
      const root = categories.find((c) => c.depth === 0) ?? categories[0]
      if (root) setSelected({ categoryId: root.id, categoryName: root.name })
    }
  }, [categories])

  const handleSelectCategory = useCallback(
    (id: string, name: string) => {
      setSelected({ categoryId: id, categoryName: name })
      // Navigate to the domain list (clearing any detail view)
      navigate({ to: `/t/${slug}/${domainPath}` })
    },
    [navigate, slug, domainPath],
  )

  return (
    <CategorySelectionContext.Provider value={selected}>
      <ResizablePanel
        className="h-[calc(100vh-8rem)]"
        left={
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b p-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {heading}
              </h2>
              {headerAction}
            </div>
            <div className="flex-1 overflow-y-auto">
              <SelectionCategoryTree
                categoryType={categoryType}
                selectedCategoryId={selected.categoryId}
                onSelectCategory={handleSelectCategory}
              />
            </div>
          </div>
        }
        right={
          <div className="flex h-full flex-col overflow-hidden">
            <Outlet />
          </div>
        }
      />
    </CategorySelectionContext.Provider>
  )
}
